import React, { useMemo, useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema, RJSFValidationError } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';

import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import { ROLE_VENDOR_USER } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import UsersService from '~/services/UsersService';

import { VENDOR_ID_MESSAGE, VENDOR_ID_PATTERN } from '~/utils/accountRoles';
import { isVendorUserCreationOnly, vendorUserCreationSchema } from '~/utils/vendorUserCreation';

import Spinner from './Spinner';

const uiSchema = {
  givenName: { 'ui:placeholder': 'John' },
  surname: { 'ui:placeholder': 'Doe' },
  email: { 'ui:widget': 'email', 'ui:placeholder': 'john@example.com' },
  password: { 'ui:widget': 'password', 'ui:placeholder': 'Secret password' },
};

type Props = {
  show: boolean;
  schema: RJSFSchema;
  isLoading?: boolean;
  userData?: unknown;
  onHide: () => void;
  userRegistrationSuccess: () => void;
};

export default function CreateNewUser(props: Props) {
  const { user } = useCurrentUser();
  const vendorOnly = isVendorUserCreationOnly(user);
  const [formData, setFormData] = useState(props.userData || (vendorOnly ? { roles: [ROLE_VENDOR_USER] } : {}));
  const { enqueueSnackbar } = useSnackbar();

  const formSchema = useMemo(() => {
    const schema = { ...props.schema };

    delete schema.title;

    const vendors = schema.properties?.vendors;
    if (typeof vendors === 'object' && typeof vendors.items === 'object' && !Array.isArray(vendors.items)) {
      schema.properties = {
        ...schema.properties,
        vendors: { ...vendors, items: { ...vendors.items, pattern: VENDOR_ID_PATTERN.source } },
      };
    }

    return vendorOnly ? vendorUserCreationSchema(schema) : schema;
  }, [props.schema, vendorOnly]);

  const transformErrors = (errors: Array<RJSFValidationError>) =>
    errors.map((error) =>
      error.name === 'pattern' && error.property?.startsWith('.vendors')
        ? { ...error, message: VENDOR_ID_MESSAGE, stack: VENDOR_ID_MESSAGE }
        : error,
    );

  const handleSubmit = async ({ formData }: IChangeEvent) => {
    try {
      const registerUser = await UsersService.registerUser(formData);
      if (registerUser) {
        props.userRegistrationSuccess();
      }
    } catch (err) {
      enqueueSnackbar(err.message || 'Error! Unable to register user.', { variant: 'error' });
    }
  };

  // Trimmed on change so ajv validates and the API receives the same value the Security tab would.
  const handleChange = ({ formData }: IChangeEvent) =>
    setFormData(
      Array.isArray(formData?.vendors)
        ? {
            ...formData,
            vendors: formData.vendors.map((vendor) => (typeof vendor === 'string' ? vendor.trim() : vendor)),
          }
        : formData,
    );

  return (
    <Dialog open={props.show} onClose={props.onHide} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>Create New User</DialogTitle>

      <DialogContent dividers>
        {props.isLoading && <Spinner />}

        {!props.isLoading && (
          <Form
            schema={formSchema}
            uiSchema={uiSchema}
            onSubmit={handleSubmit}
            formData={formData}
            onChange={handleChange}
            validator={validator}
            transformErrors={transformErrors}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
